import { generateSchema, resolveTokens } from 'wsm-schema';
import logger from 'ws-scripts/modules/logger';

/**
 * Normalizes the phone-swapping v2 array into the shape wsm-schema expects:
 * every entry carries a `department` and a `number`. The upstream service keys
 * the department as either `department` or `profitCenter` depending on version,
 * so accept both.
 *
 * @param {Array} phoneNumbers - Raw phones array from the phone-swapping service.
 * @returns {Array} - Normalized phones.
 */
const normalizePhones = (phoneNumbers) => {
	if (!Array.isArray(phoneNumbers)) {
		return [];
	}
	return phoneNumbers.map((phone) => ({
		...phone,
		department: phone.department || phone.profitCenter,
		number: phone.number || phone.phone
	}));
};

/**
 * Reconstructs a full department URL from a stored path (e.g. "/service/index.htm")
 * by resolving it against the dealer's base site URL from ADD data. Absolute
 * URLs and empty values are returned untouched.
 *
 * @param {string} path - Stored department path (or absolute URL).
 * @param {string} baseUrl - Dealer base site URL from ADD data.
 * @returns {string|undefined} - Full URL, or undefined when no path is stored.
 */
const reconstructDepartmentUrl = (path, baseUrl) => {
	if (!path) {
		return undefined;
	}
	if (/^https?:\/\//i.test(path)) {
		return path;
	}
	if (!baseUrl) {
		return path;
	}
	// ADD's url/internalUrl can be a bare host with no scheme (e.g.
	// "site.cms.web-np.dealer.com"); new URL() needs an absolute base, so add
	// https:// when the scheme is missing.
	const base = /^https?:\/\//i.test(baseUrl) ? baseUrl : `https://${baseUrl}`;
	try {
		return new URL(path, base).href;
	} catch (err) {
		return path;
	}
};

/**
 * Maps the cms-configurator schema config into wsm-schema's `options.config`
 * shape. Empty fields are left undefined so wsm-schema falls back to its
 * auto-generated values (field-level merge). Department URLs are reconstructed
 * from the stored path + dealer domain.
 *
 * @param {Object} config - The cms-configurator schema config.
 * @param {string} baseUrl - Dealer base site URL from ADD data.
 * @returns {Object} - wsm-schema options ({ config: { root, departments } }).
 */
const buildOptions = (config, baseUrl) => {
	const departments = {};
	// Pass through whatever departments the config provides; wsm-schema ignores
	// any department type it doesn't recognize.
	Object.keys(config.departments || {}).forEach((key) => {
		const dept = config.departments[key];
		if (!dept) {
			return;
		}
		departments[key] = {
			telephone: dept.telephone || undefined,
			url: reconstructDepartmentUrl(dept.departmentURL, baseUrl),
			imageURL: dept.imageURL || undefined,
			mapURL: dept.mapURL || undefined
		};
	});

	return {
		config: {
			root: {
				logoURL: config.logoURL || undefined,
				sameAs: config.sameAs?.length ? config.sameAs : undefined,
				priceRange: config.priceRange || undefined
			},
			departments
		}
	};
};

/**
 * Builds the Schema.org markup for the homepage, merging the optional
 * cms-configurator override on top of the auto-generated schema.
 *
 * Responsibilities that live entirely here (not in wsm-schema):
 *  - Resolve the configured description's tokens (`[Company Name]`, phone
 *    tokens, ...) against live ADD + phone data via `resolveTokens`, and merge
 *    the result over the auto-generated description.
 *  - Reconstruct department URLs from stored paths + the dealer domain.
 *
 * Everything else (phone priority, logo/sameAs/priceRange/department overrides,
 * empty-field fallback) is delegated to wsm-schema via `options.config`.
 *
 * @param {Object|Array} addItems - ADD data (with `phoneNumbers`) from redux.
 * @param {Object} schemaConfig - { pageAlias, config } from redux.
 * @returns {Object[]} - Schemas for SchemaRenderer (empty array when N/A).
 */
export default function generateSchemas(addItems, schemaConfig) {
	// Only the homepage renders the AutoDealer schema, and we need ADD data.
	if (schemaConfig?.pageAlias !== 'INDEX' || !addItems || !addItems.name) {
		return [];
	}

	const { config } = schemaConfig;
	const phones = normalizePhones(addItems.phoneNumbers);

	// Phone priority for the root/sales telephone: a config-supplied value wins
	// (already formatted +1XXXXXXXXXX); otherwise wsm-schema extracts SALES from
	// the phone-swapping array and formats it.
	const data = {
		...addItems,
		phoneNumbers: phones
	};
	const configSalesPhone = config?.departments?.sales?.telephone;
	if (configSalesPhone) {
		data.telephone = configSalesPhone;
	}

	// Department URLs are stored as paths and reconstructed against the dealer
	// domain from ADD: the public `url`, falling back to `internalUrl`. Sourced
	// from ADD data (in redux) rather than window, so SSR and client produce
	// identical markup.
	const siteDomain = addItems.url || addItems.internalUrl;
	const options = config ? buildOptions(config, siteDomain) : {};

	let schemas;
	try {
		schemas = generateSchema(['AutoDealer'], data, options);
	} catch (err) {
		logger.error(
			`Failed to generate AutoDealer schema for accountId=${schemaConfig?.siteId}: ${err.message}`,
			{ accountId: schemaConfig?.siteId, error: err?.message || err }
		);
		return [];
	}

	// Field-level merge for the description: when the config supplies one,
	// resolve its tokens against live ADD + phone data and override the
	// auto-generated value. An empty config description — or one that resolves
	// to an empty string — falls back to the auto-generated description.
	if (config?.description && schemas[0]) {
		try {
			const resolved = resolveTokens(config.description, {
				...addItems,
				phones
			});
			if (resolved) {
				schemas[0].description = resolved;
			}
		} catch (err) {
			logger.warn(
				`Failed to resolve schema description tokens for accountId=${schemaConfig?.siteId}: ${err.message}`,
				{ accountId: schemaConfig?.siteId, error: err?.message || err }
			);
		}
	}

	return schemas;
}
